<script lang="ts">
  import ThreadCard from "$components/forum/cards/ThreadCard.svelte";
  import ThreadListItem from "$components/forum/List/ThreadListItem.svelte";
  import ThreadCell from "$components/forum/List/ThreadCell.svelte";
  import Dialogue from "$components/+blocks/Dialogue/Dialogue.svelte";
  import api from "api";
  import {
    ForumThreadPermission,
    type ForumThreadPermissions,
  } from "@outverse-video/outverse-api-client-lib";
  import { isCommunityMember, hasPermission } from "$stores/permissions";
  import { saveToClipboard } from "$utils/clipboard";
  import { alerts } from "$stores/alert";
  import { slugify } from "$utils/strings";
  import { threadURL } from "$utils/urls.js";
  import {
    Edit,
    Eye,
    EyeSlash,
    Flag,
    Link,
    Trash,
    Unlock,
    Lock,
    Pin,
    ExpandedListFilled,
  } from "$components/+blocks/OVIcon";
  import { ForumDensity } from "app/models/shared-types";
  import type { Thread } from "models/forums";
  import type { DropdownAction } from "../+blocks/Dropdown/fields/Action.svelte";
  import { forum } from "$stores/forum";

  export let thread: Thread;
  export let communitySlug: string;
  export let showBreadCrumb: boolean = false;
  export let showChannelColour: boolean = false;
  export let isThreadUpvotesEnabled: boolean;
  export let myPermissions: ForumThreadPermissions | undefined;
  export let updateThreadHandler: ((threadUuid: string) => void) | null = null;
  export let hideThreadHandler: ((threadUuid: string, hidden: boolean) => void) | null = null;
  export let deleteThreadHandler: ((threadUuid: string) => void) | null = null;
  export let pinThreadHandler: (() => void) | null = null;
  export let lockThreadHandler: (() => void) | null = null;
  export let isQuote: boolean = false;
  export let forumViewDensity: ForumDensity = ForumDensity.Expanded;

  let isDeleteThreadDialogueOpen = false;
  let menuOptions: DropdownAction[] = [];

  $: canEdit = hasPermission(myPermissions, ForumThreadPermission.ChangeForumthread);
  $: canDelete = hasPermission(myPermissions, ForumThreadPermission.DeleteForumthread);
  $: canHide = hasPermission(myPermissions, ForumThreadPermission.HideForumthread);
  $: canPin = hasPermission(myPermissions, ForumThreadPermission.PinForumthread);
  $: canLock = hasPermission(myPermissions, ForumThreadPermission.LockForumthread);
  $: canUpvote = hasPermission(myPermissions, ForumThreadPermission.UpvoteForumthread);
  $: canMaximize = hasPermission(myPermissions, ForumThreadPermission.MaximizeForumthread);

  $: {
    menuOptions = [];
    if (canEdit && updateThreadHandler) {
      menuOptions.push({
        onClick: () => updateThreadHandler?.(thread.uuid),
        title: "Edit thread",
        icon: Edit,
      });
    }
    if (canDelete && deleteThreadHandler) {
      menuOptions.push({
        onClick: () => (isDeleteThreadDialogueOpen = true),
        title: "Delete thread",
        icon: Trash,
      });
    }
    if (canPin && pinThreadHandler) {
      menuOptions.push({
        onClick: async () => {
          let resp;
          if (thread.pinned) {
            resp = await api.communitiesThreadsDeletePin({
              communitySlug,
              uuid: thread.uuid,
            });
          } else {
            resp = await api.communitiesThreadsPin({
              communitySlug,
              uuid: thread.uuid,
            });
          }
          if (resp.status === 200) {
            pinThreadHandler?.();
          }
        },
        title: (thread.pinned ? "Unp" : "P") + "in thread",
        icon: Pin,
      });
    }
    if (canMaximize) {
      menuOptions.push({
        onClick: async () => {
          const isMaximized = thread.maximized;
          // immediately update
          thread.maximized = !thread.maximized;

          // update top-level thread object to trigger reactivity in parent
          $forum.threads[thread.uuid] = thread;

          alerts.success(`Thread card ${thread.maximized ? "maximized" : "minimized"}`);

          let resp;
          if (isMaximized) {
            resp = await api.communitiesThreadsUnmaximize({
              communitySlug,
              uuid: thread.uuid,
            });
          } else {
            resp = await api.communitiesThreadsMaximize({
              communitySlug,
              uuid: thread.uuid,
            });
          }
          // revert if failed
          if (resp.status !== 200) {
            thread.maximized = isMaximized;
            alerts.error("Failed to update thread height, please try again");
          }
        },
        title: (thread.maximized ? "Minimize" : "Maximize") + " card height",
        icon: ExpandedListFilled,
      });
    }
    if (canLock && lockThreadHandler) {
      menuOptions.push({
        onClick: async () => {
          let resp;
          if (thread.locked) {
            resp = await api.communitiesThreadsUnlock({
              communitySlug,
              uuid: thread.uuid,
            });
          } else {
            resp = await api.communitiesThreadsLock({
              communitySlug,
              uuid: thread.uuid,
            });
          }
          if (resp.status === 200) {
            lockThreadHandler?.();
          }
        },
        title: (thread.locked ? "Unlock" : "Lock") + " thread",
        icon: thread.locked ? Unlock : Lock,
      });
    }
    menuOptions.push({
      onClick: () =>
        saveToClipboard(
          threadURL(
            {
              communitySlug,
              spaceSlug: slugify(thread.forumSpace.title),
              spaceUuid: thread.forumSpace.uuid,
              threadSlug: slugify(thread.title),
              threadUuid: thread.uuid,
            },
            true
          ),
          alerts.add
        ),
      title: "Copy link",
      icon: Link,
    });
    if ($isCommunityMember) {
      menuOptions.push({
        onClick: async () => {
          try {
            await api.communitiesThreadsReport({
              communitySlug,
              uuid: thread.uuid,
            });
          } catch (Error) {
            // regardless of whether response is successful, behave as if it was
          }
          alerts.success("Thread has been reported.");
        },
        title: "Report thread",
        icon: Flag,
      });
    }
    if (canHide && hideThreadHandler) {
      menuOptions.push({
        onClick: async () => {
          let resp;
          if (thread.hidden) {
            resp = await api.communitiesThreadsUnhide({
              communitySlug,
              uuid: thread.uuid,
            });
          } else {
            resp = await api.communitiesThreadsHide({
              communitySlug,
              uuid: thread.uuid,
            });
          }
          if (resp.status === 200) {
            hideThreadHandler?.(thread.uuid, !thread.hidden);
          }
        },
        title: thread.hidden ? "Unhide thread" : "Hide thread",
        icon: thread.hidden ? Eye : EyeSlash,
      });
    }
  }

  const handleThreadDelete = async () => {
    await api.communitiesThreadsDestroy({ communitySlug, uuid: thread.uuid });
    deleteThreadHandler?.(thread.uuid);
  };
</script>

{#if thread.uuid && communitySlug}
  {#if forumViewDensity === ForumDensity.Expanded || isQuote}
    <ThreadCard
      {thread}
      {menuOptions}
      avatars={thread.repliers || []}
      {isQuote}
      {canUpvote}
      {isThreadUpvotesEnabled}
      {showBreadCrumb}
      {showChannelColour}
    />
  {:else if forumViewDensity === ForumDensity.Collapsed}
    <ThreadListItem
      {thread}
      {menuOptions}
      {canUpvote}
      {isThreadUpvotesEnabled}
      {showBreadCrumb}
      {showChannelColour}
    />
  {:else if forumViewDensity === ForumDensity.Gallery}
    <ThreadCell
      {thread}
      {menuOptions}
      avatars={thread.repliers || []}
      {canUpvote}
      {isThreadUpvotesEnabled}
    />
  {/if}
{/if}

{#if isDeleteThreadDialogueOpen}
  <Dialogue
    open
    title="Delete thread"
    message="Are you sure you want to delete this thread?"
    confirmLabel="Confirm"
    on:confirm={handleThreadDelete}
    on:cancel={() => (isDeleteThreadDialogueOpen = false)}
  />
{/if}
